import { useRef, useState } from 'react'
import { BackupError, buildBackup, downloadBackup, parseBackup, restoreBackup } from '../data/backup'

export function BackupSection({ uid }: { uid: string }) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<'export' | 'import' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function exportAll() {
    setBusy('export')
    setError(null)
    try {
      const backup = await buildBackup(uid)
      downloadBackup(backup)
      setMessage(`Exported ${backup.blocks.length} blocks.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Export failed.')
    } finally {
      setBusy(null)
    }
  }

  async function importFile(file: File) {
    setBusy('import')
    setError(null)
    setMessage(null)
    try {
      const backup = parseBackup(await file.text())
      const result = await restoreBackup(uid, backup)
      setMessage(
        `Restored ${result.blocks} blocks, ${result.periodNotes} notes and ` +
          `${result.trackerDays} tracked days${result.taxonomy ? ', plus the taxonomy' : ''}.`,
      )
    } catch (cause) {
      setError(
        cause instanceof BackupError
          ? cause.message
          : cause instanceof Error
            ? cause.message
            : 'Import failed.',
      )
    } finally {
      setBusy(null)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="p-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void exportAll()}
          disabled={busy !== null}
          className="rounded-lg border border-line-strong px-3 py-1.5 text-[14px] font-medium text-dim transition-colors duration-150 hover:text-text disabled:opacity-40"
        >
          {busy === 'export' ? 'Exporting…' : 'Export JSON'}
        </button>
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy !== null}
          className="rounded-lg border border-line px-3 py-1.5 text-[14px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text disabled:opacity-40"
        >
          {busy === 'import' ? 'Restoring…' : 'Import'}
        </button>
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void importFile(file)
          }}
        />
      </div>

      <p className="pt-2 text-[12px] leading-relaxed text-faint">
        Export writes every block, the taxonomy, settings and period notes to one file. Import
        restores by id, so re-importing the same file twice changes nothing rather than making a
        second copy.
      </p>

      {message ? <p className="pt-2 text-[13px] text-dim">{message}</p> : null}
      {error ? <p className="pt-2 text-[13px] text-danger">{error}</p> : null}
    </div>
  )
}
